import { create } from 'zustand';

import { getUploadTasks } from '@/api/thirdPartyUpload';
import type {
  ThirdPartyAggregateStatus,
  ThirdPartyUploadTask,
  ThirdPartyUploadTaskList,
} from '@/types';

const POLL_INTERVAL_MS = 5000;

interface ReleaseUploadState {
  aggregateStatus: ThirdPartyAggregateStatus;
  editable: boolean;
  tasks: ThirdPartyUploadTask[];
  loading: boolean;
}

const EMPTY_STATE: ReleaseUploadState = {
  aggregateStatus: 'not_started',
  editable: true,
  tasks: [],
  loading: false,
};

interface ThirdPartyUploadStore {
  byRelease: Record<number, ReleaseUploadState>;
  /** 拉一次最新状态，返回是否仍有未终态任务 */
  fetchOnce: (releaseId: number) => Promise<boolean>;
  /** 开始 5s 轮询；全部终态后自动停止（FR-025），重复调用幂等 */
  startPolling: (releaseId: number) => void;
  stopPolling: (releaseId: number) => void;
  getState: (releaseId: number) => ReleaseUploadState;
}

// 定时器句柄放在 store 外：它不是渲染数据，放进 state 会引起无谓的重渲染
const timers = new Map<number, ReturnType<typeof setInterval>>();

export const useThirdPartyUploadStore = create<ThirdPartyUploadStore>((set, get) => ({
  byRelease: {},

  getState: (releaseId) => get().byRelease[releaseId] ?? EMPTY_STATE,

  fetchOnce: async (releaseId) => {
    try {
      const data: ThirdPartyUploadTaskList = await getUploadTasks(releaseId);
      set((s) => ({
        byRelease: {
          ...s.byRelease,
          [releaseId]: {
            aggregateStatus: data.aggregate_status,
            editable: data.editable,
            tasks: data.tasks,
            loading: false,
          },
        },
      }));
      return data.aggregate_status === 'uploading';
    } catch {
      // 轮询期间的单次失败不清空已有数据：网络抖动不该让进度条突然消失
      set((s) => ({
        byRelease: {
          ...s.byRelease,
          [releaseId]: { ...(s.byRelease[releaseId] ?? EMPTY_STATE), loading: false },
        },
      }));
      return false;
    }
  },

  startPolling: (releaseId) => {
    if (timers.has(releaseId)) return;

    const tick = async () => {
      const stillRunning = await get().fetchOnce(releaseId);
      if (!stillRunning) get().stopPolling(releaseId);
    };

    // 先登记定时器再首拉：反过来的话，首拉若立即得到终态，stopPolling 会在
    // 定时器存在之前执行，定时器随后被建立且永不停止。
    timers.set(releaseId, setInterval(tick, POLL_INTERVAL_MS));
    void tick();
  },

  stopPolling: (releaseId) => {
    const timer = timers.get(releaseId);
    if (timer) {
      clearInterval(timer);
      timers.delete(releaseId);
    }
  },
}));
